import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Loader2,
  Save,
  Trash2,
  Pencil,
  X,
  CheckCircle2,
  CircleDashed,
  Receipt,
  Calculator,
} from "lucide-react";
import { toast } from "sonner";

import TopNav from "@/components/TopNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  adminListPlans,
  adminUnits,
  adminCreatePlan,
  adminUpdatePlan,
  adminDeletePlan,
} from "@/lib/api";

const EMPTY = {
  name: "",
  description: "",
  currency: "USD",
  base_fee_usd: 0,
  unit_event_type: "task.completed",
  unit_price_usd: 0,
  unit_bundle_size: 100,
  status: "active",
};

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = none, "new" = creating, plan obj = editing
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [{ plans }, { units }] = await Promise.all([adminListPlans(), adminUnits()]);
      setPlans(plans || []);
      setUnits(units || []);
    } catch {
      toast.error("No se pudieron cargar los planes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(EMPTY);
    setEditing("new");
  };
  const openEdit = (p) => {
    setForm({
      name: p.name || "",
      description: p.description || "",
      currency: p.currency || "USD",
      base_fee_usd: p.base_fee_usd ?? 0,
      unit_event_type: p.unit_event_type || "task.completed",
      unit_price_usd: p.unit_price_usd ?? 0,
      unit_bundle_size: p.unit_bundle_size ?? 1,
      status: p.status || "active",
    });
    setEditing(p);
  };
  const closeEdit = () => setEditing(null);

  const onSave = async () => {
    setSaving(true);
    try {
      if (editing === "new") {
        await adminCreatePlan(form);
        toast.success("Plan creado.");
      } else {
        await adminUpdatePlan(editing.id, form);
        toast.success("Plan actualizado.");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`¿Eliminar plan "${p.name}"? Los agentes asignados quedarán sin plan.`)) return;
    try {
      await adminDeletePlan(p.id);
      toast.success("Plan eliminado.");
      await load();
    } catch {
      toast.error("Error al eliminar.");
    }
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-slate-900" data-testid="admin-plans-page">
      <TopNav
        right={
          <div className="flex items-center gap-2">
            <Link to="/admin/facturacion">
              <Button variant="outline" className="rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.2em] text-slate-700 hover:bg-slate-100">
                <Receipt className="mr-2 h-3.5 w-3.5" /> Facturación
              </Button>
            </Link>
            <Link to="/admin/llm">
              <Button variant="outline" className="rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.2em] text-slate-700 hover:bg-slate-100" data-testid="nav-to-llm-btn">
                Modelos LLM
              </Button>
            </Link>
            <Button
              onClick={openNew}
              className="rounded-sm bg-slate-900 text-[11px] uppercase tracking-[0.2em] text-white hover:bg-slate-800"
              data-testid="new-plan-btn"
            >
              <Plus className="mr-2 h-3.5 w-3.5" /> Nuevo plan
            </Button>
          </div>
        }
      />
      <main className="mx-auto max-w-6xl px-6 py-10 lg:px-10">
        <p className="eyebrow text-accent-blue">Administración</p>
        <h1 className="mt-3 font-display text-4xl font-medium text-slate-900 sm:text-5xl">
          Planes de Facturación
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-slate-600">
          Configure planes parametrizados. Cada plan combina una <strong>tarifa base mensual por agente</strong>
          {" "}con un componente variable basado en eventos reales (vía webhook) o uso de chat. Los planes pueden
          asignarse a uno o varios agentes desde su editor o desde la página de Facturación.
        </p>

        {loading ? (
          <div className="mt-12 flex items-center gap-3 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin" /> Cargando…
          </div>
        ) : plans.length === 0 ? (
          <div className="mt-10 border border-dashed border-slate-300 bg-white p-10 text-center">
            <Receipt className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-4 font-display text-lg text-slate-900">Sin planes</p>
            <p className="mt-2 text-sm text-slate-600">Cree su primer plan para empezar a facturar a sus clientes.</p>
            <Button onClick={openNew} className="mt-6 rounded-sm bg-slate-900 text-[11px] uppercase tracking-[0.2em] text-white hover:bg-slate-800">
              <Plus className="mr-2 h-3.5 w-3.5" /> Crear plan
            </Button>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-2" data-testid="plans-grid">
            {plans.map((p) => {
              const u = units.find((x) => x.id === p.unit_event_type);
              return (
                <article
                  key={p.id}
                  className="border border-slate-200 bg-white p-6"
                  data-testid={`plan-card-${p.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-display text-xl font-medium text-slate-900">{p.name}</h3>
                        <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] ${
                          p.status === "active"
                            ? "border-teal-700/40 bg-teal-50 text-teal-800"
                            : "border-slate-300 bg-slate-50 text-slate-600"
                        }`}>
                          {p.status === "active" ? <CheckCircle2 className="h-3 w-3" /> : <CircleDashed className="h-3 w-3" />}
                          {p.status === "active" ? "Activo" : "Borrador"}
                        </span>
                      </div>
                      {p.description && <p className="mt-2 text-sm text-slate-600">{p.description}</p>}
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => openEdit(p)}
                        className="rounded-sm p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                        data-testid={`edit-plan-${p.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => onDelete(p)}
                        className="rounded-sm p-2 text-rose-500 hover:bg-rose-50 hover:text-rose-700"
                        data-testid={`delete-plan-${p.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>

                  <div className="mt-5 grid grid-cols-2 gap-3 border-t border-slate-100 pt-5">
                    <Mini k="Tarifa base" v={`${p.currency} ${p.base_fee_usd.toFixed(2)}/mes`} />
                    <Mini k="Unidad" v={u?.label || p.unit_event_type} />
                    <Mini k="Precio variable" v={`${p.currency} ${p.unit_price_usd.toFixed(2)} / ${p.unit_bundle_size}`} />
                    <Mini k="Fuente" v={u?.source || "—"} />
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {editing && (
          <PlanEditor
            form={form}
            update={update}
            units={units}
            saving={saving}
            onSave={onSave}
            onClose={closeEdit}
            isNew={editing === "new"}
          />
        )}
      </main>
    </div>
  );
}

function PlanEditor({ form, update, units, saving, onSave, onClose, isNew }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" data-testid="plan-editor-modal">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-3xl bg-white shadow-2xl sm:rounded-sm">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <p className="eyebrow text-accent-blue">{isNew ? "Nuevo plan" : "Editar plan"}</p>
            <h3 className="mt-1 font-display text-2xl font-medium text-slate-900">{form.name || "Sin nombre"}</h3>
          </div>
          <button onClick={onClose} className="rounded-sm p-2 text-slate-500 hover:bg-slate-100" data-testid="close-editor-btn">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-6 py-6 thin-scroll">
          <Field label="Nombre" required>
            <Input value={form.name} onChange={(e) => update("name", e.target.value)} className="rounded-sm border-slate-300" data-testid="plan-name-input" />
          </Field>
          <Field label="Descripción">
            <Textarea value={form.description} onChange={(e) => update("description", e.target.value)} rows={2} className="rounded-sm border-slate-300" data-testid="plan-desc-input" />
          </Field>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Moneda">
              <Input value={form.currency} onChange={(e) => update("currency", e.target.value.toUpperCase())} className="rounded-sm border-slate-300" />
            </Field>
            <Field label="Estado">
              <Select value={form.status} onValueChange={(v) => update("status", v)}>
                <SelectTrigger className="rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Activo</SelectItem>
                  <SelectItem value="draft">Borrador</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Tarifa base mensual (por agente)">
              <Input
                type="number" min={0} step={0.01}
                value={form.base_fee_usd}
                onChange={(e) => update("base_fee_usd", Number(e.target.value))}
                className="rounded-sm border-slate-300 font-mono"
                data-testid="plan-base-fee"
              />
            </Field>
            <Field label="Tipo de unidad facturada">
              <Select value={form.unit_event_type} onValueChange={(v) => update("unit_event_type", v)}>
                <SelectTrigger className="rounded-sm border-slate-300" data-testid="plan-unit-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {units.map((u) => (
                    <SelectItem key={u.id} value={u.id} data-testid={`plan-unit-opt-${u.id}`}>
                      {u.label} <span className="ml-2 font-mono text-xs text-slate-500">{u.source}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label={`Precio variable (${form.currency} por bundle)`}>
              <Input
                type="number" min={0} step={0.0001}
                value={form.unit_price_usd}
                onChange={(e) => update("unit_price_usd", Number(e.target.value))}
                className="rounded-sm border-slate-300 font-mono"
                data-testid="plan-unit-price"
              />
            </Field>
            <Field label="Tamaño del bundle (unidades)" hint="Ej: 100 = precio variable cobra cada 100 unidades">
              <Input
                type="number" min={1}
                value={form.unit_bundle_size}
                onChange={(e) => update("unit_bundle_size", Math.max(1, Number(e.target.value) || 1))}
                className="rounded-sm border-slate-300 font-mono"
                data-testid="plan-bundle-size"
              />
            </Field>
          </div>

          <div className="mt-6 border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-xs text-slate-600 flex items-center gap-2">
            <Calculator className="h-4 w-4 text-slate-500" />
            <span>
              Fórmula:{" "}
              <code className="font-mono">total = base × meses + (unidades × precio / bundle)</code>
            </span>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <Button variant="outline" onClick={onClose} className="rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.2em] text-slate-700 hover:bg-slate-100">
            Cancelar
          </Button>
          <Button
            onClick={onSave}
            disabled={saving || !form.name.trim()}
            className="rounded-sm bg-slate-900 text-[11px] uppercase tracking-[0.2em] text-white hover:bg-slate-800"
            data-testid="save-plan-btn"
          >
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Guardar plan
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div className="mb-5">
      <label className="text-[10px] uppercase tracking-[0.22em] text-slate-700">
        {label} {required && <span className="text-rose-600">*</span>}
      </label>
      {hint && <p className="mt-1 text-[11px] italic text-slate-500">{hint}</p>}
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Mini({ k, v }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">{k}</p>
      <p className="mt-1 truncate font-mono text-sm text-slate-800">{v}</p>
    </div>
  );
}
